import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addReportWorkspaceItem,
  archiveReportWorkspace,
  createReportWorkspace,
  deleteReportWorkspaceItem,
  getReportWorkspace,
  listReportWorkspaces,
  reorderReportWorkspaceItems,
  updateReportWorkspace,
  updateReportWorkspaceItem,
} from "./reportWorkspaceApi";
import {
  emitReportWorkspaceChanged,
  normalizeReportItems,
  readActiveWorkspaceId,
  subscribeReportWorkspaceChanged,
  writeActiveWorkspaceId,
} from "./reportStore";

function emptyWorkspace(userId, areaId) {
  return {
    id: null,
    userId: userId == null ? "" : String(userId),
    areaId: areaId == null ? "" : String(areaId),
    title: "ElectionMap report",
    note: "",
    items: [],
  };
}

function normalizeWorkspace(workspace, userId, areaId) {
  if (!workspace || typeof workspace !== "object") return emptyWorkspace(userId, areaId);
  return {
    ...workspace,
    id: workspace.id == null ? null : workspace.id,
    userId: userId == null ? "" : String(userId),
    areaId: workspace.areaId == null ? areaId : workspace.areaId,
    title: String(workspace.title || "ElectionMap report"),
    note: String(workspace.note || ""),
    items: normalizeReportItems(workspace.items),
  };
}

export function useReportWorkspace(userId, areaId) {
  const scope = useMemo(
    () => ({
      userId: userId == null ? "" : String(userId),
      areaId: areaId == null ? "" : String(areaId),
    }),
    [userId, areaId]
  );

  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState("");
  const [workspace, setWorkspace] = useState(() => emptyWorkspace(scope.userId, scope.areaId));
  const [status, setStatus] = useState("idle");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [error, setError] = useState("");
  const itemSaveVersionsRef = useRef(new Map());
  const itemSaveQueuesRef = useRef(new Map());
  const pendingItemSavesRef = useRef(0);
  const failedItemSaveFieldsRef = useRef(new Set());

  const setActiveWorkspaceId = useCallback(
    (workspaceId) => {
      const nextId = workspaceId == null ? "" : String(workspaceId);
      setActiveWorkspaceIdState(nextId);
      writeActiveWorkspaceId(scope.userId, scope.areaId, nextId);
    },
    [scope.userId, scope.areaId]
  );

  const refresh = useCallback(
    async ({ preferredWorkspaceId = null } = {}) => {
      if (!scope.userId || !scope.areaId) {
        setWorkspaces([]);
        setWorkspace(emptyWorkspace(scope.userId, scope.areaId));
        setActiveWorkspaceIdState("");
        return emptyWorkspace(scope.userId, scope.areaId);
      }
      setStatus("loading");
      setError("");
      try {
        const list = await listReportWorkspaces(scope.areaId);
        setWorkspaces(list);
        const storedId = preferredWorkspaceId || readActiveWorkspaceId(scope.userId, scope.areaId);
        const selected =
          list.find((candidate) => String(candidate.id) === String(storedId)) ||
          list[0] ||
          null;
        if (!selected) {
          const empty = emptyWorkspace(scope.userId, scope.areaId);
          setActiveWorkspaceId("");
          setWorkspace(empty);
          setStatus("ready");
          return empty;
        }
        setActiveWorkspaceId(String(selected.id));
        const loaded = normalizeWorkspace(await getReportWorkspace(scope.areaId, selected.id), scope.userId, scope.areaId);
        setWorkspace(loaded);
        setStatus("ready");
        return loaded;
      } catch (err) {
        setStatus("error");
        setError(err && err.message ? String(err.message) : "Reporty se nepodařilo načíst.");
        return emptyWorkspace(scope.userId, scope.areaId);
      }
    },
    [scope.userId, scope.areaId, setActiveWorkspaceId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await refresh();
      if (cancelled) return;
      setWorkspace(loaded);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => subscribeReportWorkspaceChanged(() => refresh()), [refresh]);

  const createWorkspace = useCallback(
    async ({ title, note = "" } = {}) => {
      setSaveStatus("saving");
      setError("");
      try {
        const created = await createReportWorkspace({
          areaId: scope.areaId,
          title: title || "ElectionMap report",
          note,
        });
        const loaded = await refresh({ preferredWorkspaceId: created && created.id });
        emitReportWorkspaceChanged({ workspaceId: created && created.id });
        setSaveStatus("saved");
        return loaded;
      } catch (err) {
        setSaveStatus("error");
        setError(err && err.message ? String(err.message) : "Report se nepodařilo vytvořit.");
        throw err;
      }
    },
    [scope.areaId, refresh]
  );

  const ensureWorkspace = useCallback(async () => {
    if (workspace && workspace.id) return workspace;
    return createWorkspace({ title: "ElectionMap report" });
  }, [workspace, createWorkspace]);

  const openWorkspace = useCallback(
    async (workspaceId) => {
      setActiveWorkspaceId(workspaceId);
      return refresh({ preferredWorkspaceId: workspaceId });
    },
    [refresh, setActiveWorkspaceId]
  );

  const renameWorkspace = useCallback(
    async (patch) => {
      if (!workspace.id) return null;
      setSaveStatus("saving");
      try {
        await updateReportWorkspace(scope.areaId, workspace.id, patch);
        const loaded = await refresh({ preferredWorkspaceId: workspace.id });
        emitReportWorkspaceChanged({ workspaceId: workspace.id });
        setSaveStatus("saved");
        return loaded;
      } catch (err) {
        setSaveStatus("error");
        setError(err && err.message ? String(err.message) : "Report se nepodařilo uložit.");
        throw err;
      }
    },
    [scope.areaId, workspace.id, refresh]
  );

  const deleteWorkspace = useCallback(async () => {
    if (!workspace.id) return;
    setSaveStatus("saving");
    try {
      await archiveReportWorkspace(scope.areaId, workspace.id);
      writeActiveWorkspaceId(scope.userId, scope.areaId, "");
      const loaded = await refresh();
      emitReportWorkspaceChanged({ workspaceId: workspace.id });
      setSaveStatus("saved");
      return loaded;
    } catch (err) {
      setSaveStatus("error");
      setError(err && err.message ? String(err.message) : "Report se nepodařilo smazat.");
      throw err;
    }
  }, [scope.userId, scope.areaId, workspace.id, refresh]);

  const addItem = useCallback(
    async (item) => {
      const target = await ensureWorkspace();
      if (!target.id) return null;
      setSaveStatus("saving");
      try {
        const savedItem = await addReportWorkspaceItem(scope.areaId, target.id, item);
        await refresh({ preferredWorkspaceId: target.id });
        emitReportWorkspaceChanged({ workspaceId: target.id });
        setSaveStatus("saved");
        return savedItem;
      } catch (err) {
        setSaveStatus("error");
        setError(err && err.message ? String(err.message) : "Položku reportu se nepodařilo uložit.");
        throw err;
      }
    },
    [scope.areaId, ensureWorkspace, refresh]
  );

  const updateItem = useCallback(
    async (itemId, patch) => {
      if (!workspace.id) return null;
      const itemKey = String(itemId);
      const fields = Object.keys(patch || {});
      const versions = new Map();
      fields.forEach((field) => {
        const fieldKey = `${itemKey}:${field}`;
        const version = (itemSaveVersionsRef.current.get(fieldKey) || 0) + 1;
        itemSaveVersionsRef.current.set(fieldKey, version);
        failedItemSaveFieldsRef.current.delete(fieldKey);
        versions.set(fieldKey, version);
      });
      const isCurrentSave = () =>
        Array.from(versions.entries()).every(
          ([fieldKey, version]) => itemSaveVersionsRef.current.get(fieldKey) === version
        );
      pendingItemSavesRef.current += 1;
      setSaveStatus("saving");
      setError("");
      setWorkspace((current) => ({
        ...current,
        items: current.items.map((item) =>
          String(item.id) === itemKey ? { ...item, ...patch } : item
        ),
      }));
      const previousSave = itemSaveQueuesRef.current.get(itemKey) || Promise.resolve();
      const requestPromise = previousSave
        .catch(() => {})
        .then(() => updateReportWorkspaceItem(scope.areaId, workspace.id, itemId, patch));
      itemSaveQueuesRef.current.set(itemKey, requestPromise);
      try {
        const item = await requestPromise;
        if (isCurrentSave()) {
          const returnedPatch = {};
          fields.forEach((field) => {
            if (item && item[field] !== undefined) returnedPatch[field] = item[field];
          });
          setWorkspace((current) => ({
            ...current,
            items: current.items.map((candidate) =>
              String(candidate.id) === itemKey ? { ...candidate, ...returnedPatch } : candidate
            ),
          }));
          versions.forEach((_, fieldKey) => failedItemSaveFieldsRef.current.delete(fieldKey));
        }
        return item;
      } catch (err) {
        if (isCurrentSave()) {
          versions.forEach((_, fieldKey) => failedItemSaveFieldsRef.current.add(fieldKey));
          setError(err && err.message ? String(err.message) : "Položku reportu se nepodařilo uložit.");
        }
        throw err;
      } finally {
        if (itemSaveQueuesRef.current.get(itemKey) === requestPromise) {
          itemSaveQueuesRef.current.delete(itemKey);
        }
        pendingItemSavesRef.current = Math.max(0, pendingItemSavesRef.current - 1);
        if (pendingItemSavesRef.current === 0) {
          setSaveStatus(failedItemSaveFieldsRef.current.size > 0 ? "error" : "saved");
        }
      }
    },
    [scope.areaId, workspace.id]
  );

  const deleteItem = useCallback(
    async (itemId) => {
      if (!workspace.id) return;
      setSaveStatus("saving");
      try {
        await deleteReportWorkspaceItem(scope.areaId, workspace.id, itemId);
        await refresh({ preferredWorkspaceId: workspace.id });
        emitReportWorkspaceChanged({ workspaceId: workspace.id });
        setSaveStatus("saved");
      } catch (err) {
        setSaveStatus("error");
        setError(err && err.message ? String(err.message) : "Položku reportu se nepodařilo smazat.");
        throw err;
      }
    },
    [scope.areaId, workspace.id, refresh]
  );

  const moveItem = useCallback(
    async (itemId, direction) => {
      if (!workspace.id) return null;
      const index = workspace.items.findIndex((item) => String(item.id) === String(itemId));
      if (index < 0) return workspace.items;
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= workspace.items.length) return workspace.items;
      const nextItems = workspace.items.slice();
      const tmp = nextItems[index];
      nextItems[index] = nextItems[targetIndex];
      nextItems[targetIndex] = tmp;
      setSaveStatus("saving");
      try {
        const items = await reorderReportWorkspaceItems(
          scope.areaId,
          workspace.id,
          nextItems.map((item) => item.id)
        );
        await refresh({ preferredWorkspaceId: workspace.id });
        emitReportWorkspaceChanged({ workspaceId: workspace.id });
        setSaveStatus("saved");
        return items;
      } catch (err) {
        setSaveStatus("error");
        setError(err && err.message ? String(err.message) : "Pořadí reportu se nepodařilo uložit.");
        throw err;
      }
    },
    [scope.areaId, workspace, refresh]
  );

  return {
    workspaces,
    workspace,
    activeWorkspaceId,
    status,
    saveStatus,
    error,
    refresh,
    createWorkspace,
    openWorkspace,
    renameWorkspace,
    deleteWorkspace,
    addItem,
    updateItem,
    deleteItem,
    moveItem,
  };
}
